import ClaudeService from "./claudeService";

// ── Creative Starts: Lumen-powered contextual starting options ──────────────
// Generates three tailored document-starting options based on the doc title
// and workspace context (room, floor, building, island, existing docs).
//
// Returns an array of 3 option objects { icon, label, desc, scaffold } on
// success, or null on failure / timeout (caller falls back to hardcoded options).

const CREATIVE_STARTS_PROMPT = `You generate exactly three starting-structure options for a document being created in a specific workspace.

You are Lumen inside NeuroQuest. Be short, direct, and specific to the context given.

Rules:
- Generate exactly three options, each genuinely distinct in approach from the others.
- If existing documents are listed, avoid suggesting something that duplicates them.
- Tailor both the label and the scaffold HTML to the document title and room context. Do not produce the same three options regardless of context.
- Scaffold must use only <h2>, <p>, <ul>, <li>, and <blockquote> tags. No other HTML tags. Keep scaffolds concise — headers and empty paragraph/list placeholders only.
- Labels must be five words or fewer.
- Descriptions must be one sentence, no period at the end, no exclamation points.
- No em dashes anywhere in your output.

Respond with ONLY a valid JSON array. Nothing before the opening bracket, nothing after the closing bracket:
[
  {
    "icon": "one emoji",
    "label": "Short label",
    "desc": "One-line description",
    "scaffold": "<h2>Section</h2><p></p>"
  },
  { ... },
  { ... }
]`;

function buildContextMessage(docTitle, roomCtx, existingDocTitles) {
  const room     = roomCtx?.room?.name     || "this room";
  const floor    = roomCtx?.floor?.name    || "";
  const building = roomCtx?.building?.name || "";
  const island   = roomCtx?.island?.name   || "";

  const locationParts = [floor, building, island].filter(Boolean);
  const location = locationParts.length ? ` (${locationParts.join(", ")})` : "";

  const existing = existingDocTitles?.filter(Boolean).length
    ? existingDocTitles.filter(Boolean).join(", ")
    : "none";

  return [
    docTitle ? `Document title: ${docTitle}` : "Document title: (untitled, no title set yet)",
    `Room: ${room}${location}`,
    `Existing documents in this room: ${existing}`,
  ].join("\n");
}

export async function fetchCreativeStarts(docTitle, roomCtx, existingDocTitles) {
  const TIMEOUT_MS = 10_000;

  const callPromise = ClaudeService.sendMessage({
    system: CREATIVE_STARTS_PROMPT,
    messages: [
      { role: "user", content: buildContextMessage(docTitle, roomCtx, existingDocTitles) },
    ],
    // Haiku is faster and cheaper for structured generation with a tight output contract
    model: "claude-haiku-4-5-20251001",
    maxTokens: 900,
  });

  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("Creative Starts: timed out after 10s")), TIMEOUT_MS)
  );

  try {
    const result = await Promise.race([callPromise, timeoutPromise]);

    if (!result.success) {
      console.warn("[CreativeStarts] Lumen call failed:", result.error);
      return null;
    }

    const cleaned = result.content.replace(/```json|```/g, "").trim();
    const parsed = JSON.parse(cleaned);

    if (!Array.isArray(parsed) || parsed.length < 3) {
      console.warn("[CreativeStarts] Unexpected response shape:", parsed);
      return null;
    }

    const three = parsed.slice(0, 3);
    const valid = three.every(o => o.icon && o.label && o.desc && typeof o.scaffold === "string");
    if (!valid) {
      console.warn("[CreativeStarts] Options missing required fields:", three);
      return null;
    }

    return three;
  } catch (err) {
    console.warn("[CreativeStarts] Failed or timed out:", err.message);
    return null;
  }
}
